import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@mui/material";
import { Spin } from "antd";
import swal from "sweetalert";
import PageHeading from "../../Components/PageHeading/PageHeading";
import { UserAuthContext } from "../../Contexts/UserAuthContext";
import moneyExchangeIcon from "../../Assets/Images/transaction-data.png";
import moment from "moment";

export default function DeveloperAiView() {
    const { AIuser } = useContext(UserAuthContext);
    const location = useLocation();
    const navigate = useNavigate();

    const [projectDetails, setProjectDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDownloading, setIsDownloading] = useState(false);
    const [expandedFiles, setExpandedFiles] = useState({});

    const projectId = location.state?.projectId || location.state?.response;
    const transactionInfo = location.state;

    const creditToLocalString = AIuser?.credits
        ? parseInt(Math.floor(AIuser?.credits)).toLocaleString()
        : "0";

    useEffect(() => {
        window.scrollTo(0, 0);
        const fetchProjectDetails = async () => {
            if (!projectId) {
                swal({
                    text: "No project ID found.",
                    icon: "error",
                    buttons: "Ok",
                    className: "modal_class_success",
                });
                navigate("/usage");
                return;
            }

            try {
                const response = await axios.get(
                    `https://backend.whalesai.com/api/v1/ai-developer/projects/${projectId}`,
                    {
                        headers: {
                            Authorization: `${localStorage.getItem("aiUser")}`,
                        },
                    }
                );

                if (response.data.success) {
                    setProjectDetails(response.data.data);
                } else {
                    throw new Error("Failed to fetch project details");
                }
            } catch (error) {
                console.error("Error:", error);
                swal({
                    text: "Failed to load project details.",
                    icon: "error",
                    buttons: "Ok",
                    className: "modal_class_success",
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchProjectDetails();
    }, [projectId, navigate]);

    const handleDownloadProject = () => {
        setIsDownloading(true);

        try {
            const downloadUrl = `https://backend.whalesai.com/api/v1/ai-developer/projects/${projectId}/download`;

            const link = document.createElement("a");
            link.href = downloadUrl;
            link.target = "_blank";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            swal({
                text: "Download started successfully!",
                icon: "success",
                buttons: "Ok",
                className: "modal_class_success",
            });
        } catch (error) {
            console.error("Error:", error);
            swal({
                text: "Failed to download project. Please try again.",
                icon: "error",
                buttons: "Ok",
                className: "modal_class_success",
            });
        } finally {
            setIsDownloading(false);
        }
    };

    const toggleFileExpansion = (index) => {
        setExpandedFiles(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const getFileIcon = (filePath) => {
        const ext = filePath.split('.').pop();
        const iconMap = {
            'js': '📜',
            'jsx': '⚛️',
            'json': '📋',
            'html': '🌐',
            'css': '🎨',
            'md': '📝',
            'env': '⚙️',
            'gitignore': '🔒'
        };
        return iconMap[ext] || '📄';
    };

    if (isLoading) {
        return (
            <div>
                <PageHeading
                    title={"GENERATED PROJECT"}
                    description={"View your generated project details and files"}
                />
                <div className="container" style={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Spin size="large" />
                </div>
            </div>
        );
    }

    if (!projectDetails) {
        return (
            <div>
                <PageHeading
                    title={"GENERATED PROJECT"}
                    description={"View your generated project details and files"}
                />
                <div className="container text-center" style={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <h5 className="text-white">Project not found</h5>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="h-100 companion-ai-container mb-4">
                <PageHeading
                    title={"GENERATED PROJECT"}
                    description={"View your generated project details and files"}
                />

                <div className="container companion-ai-main">
                    <div
                        style={{
                            maxWidth: "872px",
                            paddingInline: "12px",
                            marginInline: "auto",
                        }}
                    >
                        {/* Credits Bar */}
                        <div className="d-flex align-items-center justify-content-center centralize-credits-btn-sm mt-2 mb-4">
                            <button className="credits-button">
                                CREDITS : {creditToLocalString}
                            </button>
                            <button
                                className="buy-credits-button"
                                onClick={() => navigate("/purchase")}
                            >
                                BUY CREDITS
                            </button>
                            <button
                                onClick={() => navigate("/usage")}
                                className="money-exchange-button"
                            >
                                <img src={moneyExchangeIcon} alt="moneyExchangeIcon" />
                            </button>
                        </div>

                        {/* Project Header */}
                        <div className="text-center mb-4">
                            <div style={{
                                background: "#011C34",
                                border: "2px solid #FFF",
                                borderRadius: "16px",
                                padding: "40px 30px",
                            }}>
                                <div style={{
                                    width: "60px",
                                    height: "60px",
                                    background: projectDetails.status === "completed"
                                        ? "linear-gradient(135deg, #00ff88, #00cc70)"
                                        : "linear-gradient(135deg, #ffc107, #ff9800)",
                                    borderRadius: "50%",
                                    margin: "0 auto 25px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "30px",
                                }}>
                                    {projectDetails.status === "completed" ? "✓" : "⏳"}
                                </div>

                                <h4 className="text-white mb-3" style={{ fontWeight: "600" }}>
                                    {projectDetails.projectName}
                                </h4>

                                <p className="text-white mb-4" style={{
                                    fontSize: "14px",
                                    opacity: 0.8,
                                    maxWidth: "600px",
                                    margin: "0 auto 20px",
                                }}>
                                    {projectDetails.description}
                                </p>

                                <div style={{
                                    background: projectDetails.status === "completed"
                                        ? "rgba(0, 255, 136, 0.1)"
                                        : "rgba(255, 193, 7, 0.1)",
                                    border: projectDetails.status === "completed"
                                        ? "2px solid rgba(0, 255, 136, 0.3)"
                                        : "2px solid rgba(255, 193, 7, 0.3)",
                                    borderRadius: "10px",
                                    padding: "10px 20px",
                                    display: "inline-block",
                                }}>
                                    <span style={{
                                        color: projectDetails.status === "completed" ? "#00ff88" : "#ffc107",
                                        fontWeight: "bold",
                                        fontSize: "13px",
                                        textTransform: "uppercase",
                                        letterSpacing: "1px",
                                    }}>
                                        {projectDetails.status}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Transaction Info */}
                        {transactionInfo && (
                            <div className="mb-3">
                                <div style={{
                                    background: "#011C34",
                                    border: "2px solid #FFF",
                                    borderRadius: "12px",
                                    padding: "15px",
                                }}>
                                    <div className="row">
                                        <div className="col-md-6 mb-2">
                                            <p className="text-white mb-1" style={{ fontSize: "13px" }}>
                                                <strong>Transaction ID:</strong> {transactionInfo.transactionId}
                                            </p>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <p className="text-white mb-1" style={{ fontSize: "13px" }}>
                                                <strong>Credits Used:</strong> {transactionInfo.totalCreditsUsed}
                                            </p>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <p className="text-white mb-1" style={{ fontSize: "13px" }}>
                                                <strong>Date Created:</strong> {moment(projectDetails.createdAt).format("DD-MM-YYYY HH:mm:ss")}
                                            </p>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <p className="text-white mb-0" style={{ fontSize: "13px" }}>
                                                <strong>Time Taken:</strong> {projectDetails.actualTime} minute(s)
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Tech Stack */}
                        <div className="mb-3">
                            <div style={{
                                background: "#011C34",
                                border: "2px solid #FFF",
                                borderRadius: "12px",
                                padding: "15px",
                            }}>
                                <h6 className="text-white mb-3">
                                    <strong>Tech Stack</strong>
                                </h6>
                                <div className="row">
                                    {projectDetails.specifications?.generateComponents?.frontend && projectDetails.specifications?.stack?.frontend && (
                                        <div className="col-6 mb-2">
                                            <p className="text-white mb-0" style={{ fontSize: "13px" }}>
                                                <strong>Frontend:</strong> {projectDetails.specifications.stack.frontend}
                                            </p>
                                        </div>
                                    )}
                                    {projectDetails.specifications?.generateComponents?.backend && projectDetails.specifications?.stack?.backend && (
                                        <div className="col-6 mb-2">
                                            <p className="text-white mb-0" style={{ fontSize: "13px" }}>
                                                <strong>Backend:</strong> {projectDetails.specifications.stack.backend}
                                            </p>
                                        </div>
                                    )}
                                    {projectDetails.specifications?.stack?.database && (
                                        <div className="col-6 mb-2">
                                            <p className="text-white mb-0" style={{ fontSize: "13px" }}>
                                                <strong>Database:</strong> {projectDetails.specifications.stack.database}
                                            </p>
                                        </div>
                                    )}
                                    {projectDetails.specifications?.generateComponents?.mobile && projectDetails.specifications?.stack?.mobile && projectDetails.specifications.stack.mobile !== "none" && (
                                        <div className="col-6 mb-2">
                                            <p className="text-white mb-0" style={{ fontSize: "13px" }}>
                                                <strong>Mobile:</strong> {projectDetails.specifications.stack.mobile}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Features */}
                        {projectDetails.specifications?.features && projectDetails.specifications.features.length > 0 && (
                            <div className="mb-3">
                                <div style={{
                                    background: "#011C34",
                                    border: "2px solid #FFF",
                                    borderRadius: "12px",
                                    padding: "15px",
                                }}>
                                    <h6 className="text-white mb-3">
                                        <strong>Features</strong>
                                    </h6>
                                    <ul className="text-white" style={{ fontSize: "13px", marginLeft: "20px" }}>
                                        {projectDetails.specifications.features.map((feature, index) => (
                                            <li key={index} className="mb-1">{feature}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}

                        {/* Project Statistics */}
                        <div className="mb-3">
                            <div style={{
                                background: "#011C34",
                                border: "2px solid #FFF",
                                borderRadius: "12px",
                                padding: "15px",
                            }}>
                                <h6 className="text-white mb-3">
                                    <strong>Project Statistics</strong>
                                </h6>
                                <div className="row">
                                    <div className="col-md-4 mb-2">
                                        <p className="text-white mb-0" style={{ fontSize: "13px" }}>
                                            <strong>Total Files:</strong> {projectDetails.generatedFiles?.length || 0}
                                        </p>
                                    </div>
                                    <div className="col-md-4 mb-2">
                                        <p className="text-white mb-0" style={{ fontSize: "13px" }}>
                                            <strong>Progress:</strong> {projectDetails.progress}%
                                        </p>
                                    </div>
                                    <div className="col-md-4 mb-2">
                                        <p className="text-white mb-0" style={{ fontSize: "13px" }}>
                                            <strong>API Calls:</strong> {projectDetails.metadata?.apiCallsUsed || 0}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Generated Files */}
                        {projectDetails.generatedFiles && projectDetails.generatedFiles.length > 0 && (
                            <div className="mb-3">
                                <div style={{
                                    background: "#011C34",
                                    border: "2px solid #FFF",
                                    borderRadius: "12px",
                                    padding: "15px",
                                }}>
                                    <h6 className="text-white mb-3">
                                        <strong>Generated Files ({projectDetails.generatedFiles.length})</strong>
                                    </h6>
                                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                                        {projectDetails.generatedFiles.map((file, index) => (
                                            <div
                                                key={index}
                                                className="mb-2"
                                                style={{
                                                    background: "#001f3f",
                                                    borderRadius: "8px",
                                                    padding: "10px",
                                                }}
                                            >
                                                <div
                                                    className="d-flex justify-content-between align-items-center"
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => toggleFileExpansion(index)}
                                                >
                                                    <p className="text-white mb-0" style={{ fontSize: "13px" }}>
                                                        <span style={{ marginRight: "8px" }}>{getFileIcon(file.filePath)}</span>
                                                        <strong>{file.filePath}</strong>
                                                    </p>
                                                    <span className="text-white">
                                                        {expandedFiles[index] ? "▼" : "►"}
                                                    </span>
                                                </div>

                                                {expandedFiles[index] && (
                                                    <div className="mt-2">
                                                        <p className="text-white mb-1" style={{ fontSize: "12px" }}>
                                                            <strong>Type:</strong> {file.fileType}
                                                        </p>
                                                        <p className="text-white mb-1" style={{ fontSize: "12px" }}>
                                                            <strong>Size:</strong> {file.size} bytes
                                                        </p>
                                                        <div
                                                            style={{
                                                                background: "#000",
                                                                borderRadius: "5px",
                                                                padding: "10px",
                                                                marginTop: "10px",
                                                                maxHeight: "200px",
                                                                overflowY: "auto"
                                                            }}
                                                        >
                                                            <pre
                                                                style={{
                                                                    color: "#00ff00",
                                                                    fontSize: "11px",
                                                                    margin: 0,
                                                                    whiteSpace: "pre-wrap",
                                                                    wordBreak: "break-word"
                                                                }}
                                                            >
                                                                {file.content}
                                                            </pre>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Download Button */}
                        {projectDetails.status === "completed" && (
                            <div className="text-center mb-5 mt-4">
                                <div style={{
                                    background: "#011C34",
                                    border: "2px solid #00ff88",
                                    borderRadius: "16px",
                                    padding: "40px 30px",
                                    maxWidth: "500px",
                                    margin: "0 auto",
                                }}>
                                    <div style={{
                                        width: "70px",
                                        height: "70px",
                                        background: "linear-gradient(135deg, #00ff88, #00cc70)",
                                        borderRadius: "50%",
                                        margin: "0 auto 25px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "35px",
                                    }}>
                                        ✓
                                    </div>

                                    <h4 className="text-white mb-2" style={{ fontWeight: "600" }}>
                                        Your Project is Ready!
                                    </h4>

                                    <p className="text-white mb-4" style={{
                                        fontSize: "14px",
                                        opacity: 0.8,
                                    }}>
                                        Download your project files and start developing
                                    </p>

                                    <Button
                                        sx={{
                                            padding: "12px 40px",
                                            background: "linear-gradient(135deg, #00ff88, #00cc70)!important",
                                            color: "#000!important",
                                            fontWeight: "bold",
                                            fontSize: "16px",
                                            borderRadius: "10px",
                                            textTransform: "none",
                                            "&:hover": {
                                                background: "linear-gradient(135deg, #00cc70, #00ff88)!important",
                                                transform: "translateY(-2px)",
                                                boxShadow: "0 5px 20px rgba(0, 255, 136, 0.4)",
                                            },
                                            transition: "all 0.3s ease",
                                        }}
                                        onClick={handleDownloadProject}
                                        variant="contained"
                                        disabled={isDownloading}
                                    >
                                        {isDownloading ? "Downloading..." : "Download Project"}
                                    </Button>
                                </div>
                                <p className="text-warning text-center pt-4 mb-0">
                                    Please download the project within 30 days, as it will
                                    be deleted after that time.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}